import React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Autocomplete, TextField } from "@mui/material";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { handleCheckOverlappingSchedulesForSharedProducts } from "./SiteAboutPageUtils/handleCheckOverlappingSchedulesForSharedProducts";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { getNumbersAfterAboutSite } from "./SiteAboutPageUtils/getNumbersAfterAboutSite";
import { fetchAddCameraInfo } from "../SitesPage/SitesPageUtils/fetchAddCameraInfo";

export const EditActiveProductsDialog = () => {
  const navigate = useNavigate();
  const {
    siteSchedules,
    editActiveProductsDialogOpen,
    setEditActiveProductsDialogOpen,
    setSiteSchedules,
    setScheduleChangesMade,
    schedulesIndex,
  } = useSiteAboutStore();

  const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);
  const [productOptions, setProductOptions] = React.useState<string[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  // Memoize the localStorage data to prevent infinite re-renders
  const products = React.useMemo(() => {
    try {
      const productsData = localStorage.getItem("products");
      if (!productsData) return [];
      return JSON.parse(productsData);
    } catch (error) {
      return [];
    }
  }, []);

  const productIds = React.useMemo(() => {
    const map = new Map<string, string>();
    if (products && Array.isArray(products)) {
      products.forEach((element: any) => {
        if (element && element.name && element.id) {
          map.set(element.id, `${element.name}`);
        }
      });
    }
    return map;
  }, [products]);

  const productNames = React.useMemo(() => {
    const map = new Map<string, string>();
    if (products && Array.isArray(products)) {
      products.forEach((element: any) => {
        if (element && element.name && element.id) {
          map.set(`${element.name}`, element.id);
        }
      });
    }
    return map;
  }, [products]);

  // When dialog opens, fetch current site's products so we always show the correct list
  React.useEffect(() => {
    if (!editActiveProductsDialogOpen) return;

    let cancelled = false;
    setIsLoading(true);

    const siteId = getNumbersAfterAboutSite(
      typeof window !== "undefined" ? window.location.href : ""
    );

    if (!siteId) {
      // No site context (e.g. unexpected URL); fall back to localStorage
      try {
        const selectedData = localStorage.getItem("selectedProducts");
        const selectedProductsFromStorage = selectedData
          ? JSON.parse(selectedData)
          : [];
        if (
          Array.isArray(selectedProductsFromStorage) &&
          selectedProductsFromStorage.length > 0
        ) {
          const options = selectedProductsFromStorage
            .map((id: any) => productIds.get(id))
            .filter(Boolean) as string[];
          if (!cancelled) {
            setProductOptions(options);
            if (
              siteSchedules?.[schedulesIndex]?.product
            ) {
              setSelectedProducts(
                siteSchedules[schedulesIndex].product
                  .map((id: any) => productIds.get(id))
                  .filter(Boolean) as string[]
              );
            }
          }
        } else {
          if (!cancelled) setProductOptions([]);
        }
      } catch {
        if (!cancelled) setProductOptions([]);
      }
      if (!cancelled) setIsLoading(false);
      return;
    }

    fetchAddCameraInfo(siteId, navigate).then((data) => {
      if (cancelled) return;
      try {
        if (!products || !Array.isArray(products) || products.length === 0) {
          setProductOptions([]);
          setIsLoading(false);
          return;
        }
        const siteProductIds =
          data?.initial_data?.products && Array.isArray(data.initial_data.products)
            ? data.initial_data.products
            : [];
        const options = siteProductIds
          .map((id: any) => productIds.get(id))
          .filter(Boolean) as string[];
        setProductOptions(options);
        if (
          siteSchedules &&
          siteSchedules[schedulesIndex] &&
          siteSchedules[schedulesIndex].product
        ) {
          const initialSelected = siteSchedules[schedulesIndex].product
            .map((id: any) => productIds.get(id))
            .filter(Boolean) as string[];
          setSelectedProducts(initialSelected);
        } else {
          setSelectedProducts([]);
        }
      } catch {
        setProductOptions([]);
        setSelectedProducts([]);
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    editActiveProductsDialogOpen,
    products,
    productIds,
    siteSchedules,
    schedulesIndex,
    navigate,
  ]);

  // Don't render if still loading or if we don't have valid products list
  if (isLoading || !products || products.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={editActiveProductsDialogOpen}
      onClose={() => setEditActiveProductsDialogOpen(false)}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar sx={{ background: "#001943" }}>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Edit Active Products
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <div>
          <div className="h-[200px]">
            <div className=" bg-white">
              <div>
                <div className="pt-[20px] pb-[30px]">
                  <Autocomplete
                    multiple
                    value={selectedProducts}
                    disablePortal
                    options={productOptions}
                    onChange={(event, value: any) => {
                      setSelectedProducts(value);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Product(s)"
                        autoComplete="off"
                        sx={{ width: 400 }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex pr-[20px] pt-[20px]" style={{ display: "flex" }}>
          <div style={{ marginLeft: "auto" }}>
            <OrangeTextButton
              onClick={() => {
                setEditActiveProductsDialogOpen(false);
              }}
              text="Cancel"
              disabled={false}
            />
          </div>
          &nbsp;&nbsp;&nbsp;
          <div>
            <OrangeTextButton
              onClick={() => {
                try {
                  const newSiteSchedules = siteSchedules;
                  newSiteSchedules[schedulesIndex].product = selectedProducts
                    .map((name: string) => productNames.get(name))
                    .filter(Boolean) as string[]; // Remove any undefined values

                  setSiteSchedules(newSiteSchedules);
                  setScheduleChangesMade(true);
                  setEditActiveProductsDialogOpen(false);
                  handleCheckOverlappingSchedulesForSharedProducts(
                    newSiteSchedules
                  );
                } catch (error) {
                  // Error handling - you might want to show an error message to the user here
                }
              }}
              text="Ok"
              disabled={false}
            />
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
